import { memo, useEffect, useRef, useState } from "react";

import { ISurveyTranslation } from "../../../@types/admin/AdminSurveyTranslation";
import { TranslationInput } from "./AdminTranslations.styles";

/**
 * How long typing must pause before the edit is reported to the page. The
 * page owns the unsaved count and the Save button, so every keystroke used to
 * re-derive the whole translation set — see the note on the component.
 */
const COMMIT_DEBOUNCE_MS = 200;

interface ITranslationValueCellProps {
  translation: ISurveyTranslation;
  disabled: boolean;
  onCommit: (translation: ISurveyTranslation, value: string) => void;
}

/**
 * The editable Value cell of the translation grid (EN-5163).
 *
 * The cell owns what you are typing; the page only hears about it once you
 * pause. It used to be the other way round — every keystroke went straight
 * into the page's `edits` map, which re-mapped every translation in the
 * survey, re-ran the search and the paging, and handed `useReactTable` a
 * brand-new `columns` array, so the whole grid was rebuilt between one letter
 * and the next and the field visibly stalled (EN-5146 UI feedback).
 *
 * `dirty` is computed here against the server's value rather than read from
 * the page's map, so the highlight still lands on the first keystroke.
 *
 * The page remounts these (via `key`) when the rows are discarded, saved or
 * the survey changes — that is what clears the local value.
 */
export const TranslationValueCell = memo(function TranslationValueCell({
  translation,
  disabled,
  onCommit,
}: ITranslationValueCellProps) {
  const serverValue = translation.value ?? "";
  const [value, setValue] = useState(serverValue);

  // Kept in a ref so the debounce effect doesn't restart when the page
  // re-renders with a new closure.
  const commitRef = useRef(onCommit);
  commitRef.current = onCommit;

  /**
   * Whether this cell has been typed in at all. Without it the mount pass of
   * the effect would report every visible row as an edit; with it, a value
   * typed back to the server's is still reported — that is what clears the
   * row from the unsaved set.
   */
  const touched = useRef(false);

  useEffect(() => {
    if (!touched.current) return undefined;
    const timer = setTimeout(
      () => commitRef.current(translation, value),
      COMMIT_DEBOUNCE_MS
    );
    return () => clearTimeout(timer);
  }, [value, translation]);

  return (
    <TranslationInput
      aria-label={`${translation.key} (${translation.languageCode})`}
      dirty={value !== serverValue}
      disabled={disabled}
      value={value}
      onChange={(e) => {
        touched.current = true;
        setValue(e.target.value);
      }}
      // Leaving the field shouldn't wait out the debounce — the page may be
      // about to read the edits (Save, or the unsaved-changes guard).
      onBlur={() => {
        if (touched.current) onCommit(translation, value);
      }}
    />
  );
});
